import { useEffect, useState } from 'react';

const PERFORMANCE_MODE_EVENT = 'ntp:performance-mode-change';
const LOW_POWER_CLASS = 'low-power-mode';

const mediaQueryMatches = (query) => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(query).matches;
};

const connectionRequestsReducedWork = () => {
  if (typeof navigator === 'undefined') return false;
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  return Boolean(connection?.saveData || ['slow-2g', '2g'].includes(connection?.effectiveType));
};

const readInitialMode = () => {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains(LOW_POWER_CLASS)
    || mediaQueryMatches('(prefers-reduced-motion: reduce)')
    || connectionRequestsReducedWork();
};

const setPerformanceClass = (enabled) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle(LOW_POWER_CLASS, enabled);
  root.dataset.ntpPerformance = enabled ? 'reduced' : 'full';
  window.dispatchEvent(new CustomEvent(PERFORMANCE_MODE_EVENT, {
    detail: { lowPower: enabled },
  }));
};

/**
 * Detects OS/browser reduced-motion, Data Saver, slow connections, and the
 * Battery Status API's low-battery signal. Unsupported APIs simply fall back
 * to the normal visual mode.
 */
export const initPerformanceMode = () => {
  if (typeof window === 'undefined') return () => {};

  let battery = null;
  let disposed = false;
  const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

  const update = () => {
    if (disposed) return;
    const batteryLow = Boolean(battery && !battery.charging && battery.level <= 0.15);
    setPerformanceClass(Boolean(
      reducedMotion?.matches
      || connection?.saveData
      || ['slow-2g', '2g'].includes(connection?.effectiveType)
      || batteryLow,
    ));
  };

  reducedMotion?.addEventListener?.('change', update);
  connection?.addEventListener?.('change', update);

  if (typeof navigator.getBattery === 'function') {
    navigator.getBattery().then((result) => {
      if (disposed) return;
      battery = result;
      battery.addEventListener?.('chargingchange', update);
      battery.addEventListener?.('levelchange', update);
      update();
    }).catch(() => undefined);
  }

  update();

  return () => {
    disposed = true;
    reducedMotion?.removeEventListener?.('change', update);
    connection?.removeEventListener?.('change', update);
    battery?.removeEventListener?.('chargingchange', update);
    battery?.removeEventListener?.('levelchange', update);
  };
};

export const useLowPowerMode = () => {
  const [lowPower, setLowPower] = useState(readInitialMode);

  useEffect(() => {
    const onModeChange = (event) => {
      setLowPower(Boolean(event.detail?.lowPower));
    };

    window.addEventListener(PERFORMANCE_MODE_EVENT, onModeChange);
    setLowPower(readInitialMode());
    return () => window.removeEventListener(PERFORMANCE_MODE_EVENT, onModeChange);
  }, []);

  return lowPower;
};

